import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../config/api';

const AccessControl = () => {
  const { token } = useAuth();
  const [violations, setViolations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      fetchViolations();
    }
  }, [token]);

  const fetchViolations = async () => {
    try {
      setLoading(true);
      const data = await api.getViolations(token);
      setViolations(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching violations:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-cyber-dark flex items-center justify-center">
        <div className="text-white text-xl">Loading access control...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cyber-dark">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white">Access Control</h1>
          <p className="text-gray-400 mt-1">Monitor and manage access policies</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-cyber-card border border-gray-800 rounded-lg p-6">
            <h3 className="text-sm text-gray-400 mb-1">Total Violations</h3>
            <p className="text-3xl font-bold text-white">{violations.length}</p>
          </div>
          <div className="bg-cyber-card border border-gray-800 rounded-lg p-6">
            <h3 className="text-sm text-gray-400 mb-1">Today</h3>
            <p className="text-3xl font-bold text-red-400">
              {violations.filter(v => 
                new Date(v.timestamp).toDateString() === new Date().toDateString()
              ).length}
            </p>
          </div>
          <div className="bg-cyber-card border border-gray-800 rounded-lg p-6">
            <h3 className="text-sm text-gray-400 mb-1">Active Policies</h3>
            <p className="text-3xl font-bold text-green-400">12</p>
          </div>
        </div>

        <div className="bg-cyber-card border border-gray-800 rounded-lg p-6 mb-6">
          <h3 className="text-xl font-semibold text-white mb-4">Access Policies</h3>
          <div className="space-y-3">
            {[
              { name: 'Zero-Trust Access', status: 'Active', rules: 15 },
              { name: 'Multi-Factor Authentication', status: 'Active', rules: 8 },
              { name: 'Geographic Restrictions', status: 'Active', rules: 5 },
              { name: 'Time-Based Access', status: 'Inactive', rules: 3 },
            ].map((policy, index) => (
              <div key={index} className="bg-cyber-darker border border-gray-700 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-white font-medium">{policy.name}</h4>
                    <p className="text-sm text-gray-400">{policy.rules} rules configured</p>
                  </div>
                  <span className={`px-3 py-1 text-xs rounded ${
                    policy.status === 'Active'
                      ? 'bg-green-500/20 text-green-400'
                      : 'bg-gray-500/20 text-gray-400'
                  }`}>
                    {policy.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-cyber-card border border-gray-800 rounded-lg overflow-hidden">
          <div className="p-6 border-b border-gray-700">
            <h3 className="text-xl font-semibold text-white">Recent Violations</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-cyber-darker">
                <tr>
                  <th className="text-left py-4 px-4 text-sm font-medium text-gray-400">User</th>
                  <th className="text-left py-4 px-4 text-sm font-medium text-gray-400">Policy</th>
                  <th className="text-left py-4 px-4 text-sm font-medium text-gray-400">Action</th>
                  <th className="text-left py-4 px-4 text-sm font-medium text-gray-400">Resource</th>
                  <th className="text-left py-4 px-4 text-sm font-medium text-gray-400">Time</th>
                </tr>
              </thead>
              <tbody>
                {violations.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="text-center py-8 text-gray-400">
                      No violations found
                    </td>
                  </tr>
                ) : (
                  violations.map((violation) => (
                    <tr key={violation.id} className="border-b border-gray-700 hover:bg-cyber-darker transition-colors">
                      <td className="py-4 px-4 text-sm text-white font-medium">{violation.user_name}</td>
                      <td className="py-4 px-4 text-sm text-gray-300">{violation.policy}</td>
                      <td className="py-4 px-4 text-sm text-gray-300">{violation.action}</td>
                      <td className="py-4 px-4 text-sm text-gray-400">{violation.resource}</td>
                      <td className="py-4 px-4 text-sm text-gray-400">
                        {new Date(violation.timestamp).toLocaleString()}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccessControl;